
import { useState, useRef, useEffect, RefObject } from 'react';
import { VideoFeatures } from '@/store/videoFeatures';

/**
 * Hook to manage the visibility of video controls
 */
export function useControlsVisibility(
  containerRef: RefObject<HTMLDivElement>, 
  isPlaying: boolean, 
  features: VideoFeatures,
  showAutoplayOverlay: boolean
) {
  const [showControls, setShowControls] = useState(true);
  const controlsTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Handle mouse movement to show/hide controls
  useEffect(() => {
    const container = containerRef.current;
    if (!container || features.disableControls) {
      if (features.disableControls) {
        setShowControls(false);
      }
      return;
    }
    
    // Don't hide controls if autoplay overlay is shown
    if (features.autoplayOverlay?.enabled && showAutoplayOverlay) {
      return;
    }
    
    const handleMouseMove = () => {
      setShowControls(true);
      
      // Reset the timer
      if (controlsTimerRef.current) {
        clearTimeout(controlsTimerRef.current);
      }
      
      // Set new timer to hide controls after 3 seconds of inactivity
      if (isPlaying) {
        controlsTimerRef.current = setTimeout(() => {
          setShowControls(false);
        }, 3000);
      }
    };
    
    const handleMouseLeave = () => {
      if (isPlaying) {
        setShowControls(false);
      }
    };
    
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);
    
    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
      
      if (controlsTimerRef.current) {
        clearTimeout(controlsTimerRef.current);
      }
    };
  }, [isPlaying, features.disableControls, features.autoplayOverlay?.enabled, showAutoplayOverlay, containerRef]);

  return {
    showControls,
    setShowControls
  };
}
